import {showNotification} from 'platform/components';

import {useSelector} from 'react-redux';

import {any, isNotNil, last} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty, isTrue} from 'ramda-adjunct';

import {useReadCodeQuery} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {
  interestApi,
  useConvertForBuyingVehicleMutation,
  useDeleteMinisaleMutation,
  useGetInterestV2Query,
  useGetMinipurchaseQuery,
  useGetMinisaleQuery,
  useGetOfferedVehiclesListQuery,
} from '@dms/api/finance';
import {ApiError, EMPTY_UTM_PARAMETERS} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {businessCaseRoutes} from '@dms/routes';
import {handleApiError, useMandatoryFields, useMandatoryFieldsErrorDialog} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import {getLastActiveBusinessCase} from '../utils/getLastActiveBusinessCase';

const TTFR_POLLING_INTERVAL_MS = 60_000;

export function useInterestData(interestId: string) {
  const navigate = useNavigate();
  const {isMandatoryFieldsEnabled} = useMandatoryFields();

  const {data: cachedInterest} = useSelector(
    interestApi.endpoints.getInterestV2.select({id: interestId})
  );
  const isTtfrFrozen =
    isNotNil(cachedInterest?.firstResponseAt) && isNotNil(cachedInterest?.ttfrBusinessMinutes);

  const {
    data: interest,
    isLoading: isInterestLoading,
    isError: isInterestErrored,
  } = useGetInterestV2Query(
    {id: interestId},
    {pollingInterval: isTtfrFrozen ? undefined : TTFR_POLLING_INTERVAL_MS}
  );

  const {
    data: customer,
    isLoading: isCustomerLoading,
    isError: isCustomerErrored,
  } = useGetCustomerV2Query(
    {customerId: interest?.customerId ?? ''},
    {
      skip: isNilOrEmpty(interest?.customerId),
    }
  );

  const {data: readCode} = useReadCodeQuery(
    {codelistId: 'interest_source', codeId: interest?.sourceCodeId ?? ''},
    {skip: !interest?.sourceCodeId}
  );

  const minisaleId = interest?.minisalesIds ? last(interest.minisalesIds) : null;
  const {data: miniSale, isFetching: isMiniSaleFetching} = useGetMinisaleQuery(
    {
      minisaleId: minisaleId!,
    },
    {
      skip: isNilOrEmpty(minisaleId),
      // ensures correct functionality of one-way autoSave - after arriving at this page we force a re-fetch
      refetchOnMountOrArgChange: true,
    }
  );

  const miniPurchaseId = interest?.minipurchasesIds ? last(interest.minipurchasesIds) : null;
  const {data: miniPurchase, isFetching: isMinipurchaseFetching} = useGetMinipurchaseQuery(
    {minipurchaseId: miniPurchaseId!},
    {
      skip: isNilOrEmpty(miniPurchaseId),
      // ensures correct functionality of one-way autoSave - after arriving at this page we force a re-fetch
      refetchOnMountOrArgChange: true,
    }
  );

  const {data: offeredVehiclesList} = useGetOfferedVehiclesListQuery(
    {body: {vehicleIds: miniPurchase?.offeredVehiclesIds ?? ['']}},
    {skip: isNilOrEmpty(miniPurchase?.offeredVehiclesIds)}
  );

  const [convertForBuyingVehicle, {isLoading: isConvertForBuyingVehicleLoading}] =
    useConvertForBuyingVehicleMutation();

  const [, {isLoading: isDeleteMinisaleLoading}] = useDeleteMinisaleMutation();

  const {handleMandatoryFieldsError, isMandatoryFieldsError} = useMandatoryFieldsErrorDialog();

  const onCreateBuyingBusinessCase = () => {
    convertForBuyingVehicle({
      interestId,
      validateMandatoryFields: isMandatoryFieldsEnabled(undefined),
    })
      .unwrap()
      .then((response) => {
        const lastActiveBusinessCase = getLastActiveBusinessCase(response?.activeBusinessCases);

        if (!lastActiveBusinessCase?.businessCaseId) {
          showNotification.error();
          return;
        }

        navigate(
          composePath(businessCaseRoutes.overview, {
            params: {
              id: lastActiveBusinessCase.businessCaseId,
            },
          })
        );

        showNotification.success(i18n.t('general.notifications.success'));
      })
      .catch((error: ApiError) => {
        if (isMandatoryFieldsError(error)) {
          return handleMandatoryFieldsError(error.data?.validationErrors ?? []);
        }

        return handleApiError(error);
      });
  };

  const isLoading = any(isTrue, [
    isInterestLoading,
    isCustomerLoading,
    isConvertForBuyingVehicleLoading,
  ]);

  const isActionsDisabled = any(isTrue, [
    isInterestLoading,
    isCustomerLoading,
    isConvertForBuyingVehicleLoading,
    isMiniSaleFetching,
    isMinipurchaseFetching,
    isDeleteMinisaleLoading,
  ]);

  const isError = any(isTrue, [isInterestErrored, isCustomerErrored]);

  return {
    interest,
    customer,
    utm: interest?.utm ?? EMPTY_UTM_PARAMETERS,
    readCode,
    miniSale: isNotNil(minisaleId) && isNotNil(miniSale) ? miniSale : null,
    miniPurchase: isNotNil(miniPurchaseId) && isNotNil(miniPurchase) ? miniPurchase : null,
    offeredVehiclesList:
      isNotNilOrEmpty(miniPurchase?.offeredVehiclesIds) && isNotNil(offeredVehiclesList)
        ? offeredVehiclesList
        : null,
    onCreateBuyingBusinessCase,
    isLoading,
    isError,
    isActionsDisabled,
  };
}
